import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, ChevronUp, ChevronDown, ChevronsUpDown, Phone, MapPin, Download, ArrowRightLeft, CheckSquare, Square } from 'lucide-react';
import { customerAPI, agentAPI } from '../services/api';
import useAuthStore from '../stores/authStore';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useDateFormat } from '../hooks/useDateFormat';

const STATUS_COLORS = {
  'Done': 'bg-green-100 text-green-800',
  'done': 'bg-green-100 text-green-800',
  'Pending Installation': 'bg-blue-100 text-blue-800',
  'Pending Instalation': 'bg-blue-100 text-blue-800',
  'In Process': 'bg-yellow-100 text-yellow-800',
  'Standby': 'bg-purple-100 text-purple-800',
  'Canceled': 'bg-red-100 text-red-800',
  'Cancelled': 'bg-red-100 text-red-800',
  'Future Fiber': 'bg-cyan-100 text-cyan-800',
  'Not Processed': 'bg-gray-100 text-gray-800',
  'Not Procesed': 'bg-gray-100 text-gray-800',
  'Reminder': 'bg-pink-100 text-pink-800',
  'Suspended': 'bg-red-100 text-red-800',
  'Temporary Disconnected': 'bg-orange-100 text-orange-800',
  'Temporarily Disconnected': 'bg-orange-100 text-orange-800',
  'Temporarily disconnected': 'bg-orange-100 text-orange-800',
  'temporary disconnected': 'bg-orange-100 text-orange-800',
  'Active': 'bg-green-100 text-green-800',
  'active': 'bg-green-100 text-green-800'
};

const Customers = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { formatDate } = useDateFormat();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(() => parseInt(searchParams.get('page')) || 1);
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [status, setStatus] = useState(() => searchParams.get('status') || '');
  const [area, setArea] = useState(() => searchParams.get('area') || '');
  const [sortBy, setSortBy] = useState(() => searchParams.get('sortBy') || 'createdAt');
  const [sortOrder, setSortOrder] = useState(() => searchParams.get('sortOrder') || 'DESC');

  // Agent state
  const [selectedIds, setSelectedIds] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [batchStatus, setBatchStatus] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const isNotProcessed = status === 'Not Processed';
  const canAgent = ['admin', 'manager'].includes(user?.role);
  const showAgentBar = isNotProcessed && canAgent;

  // Clear selections when filter changes
  useEffect(() => {
    setSelectedIds([]);
  }, [status, search, area, page]);

  // Sync filters to URL params
  useEffect(() => {
    const params = {};
    if (status) params.status = status;
    if (search) params.search = search;
    if (area) params.area = area;
    if (page > 1) params.page = page;
    if (sortBy !== 'createdAt') params.sortBy = sortBy;
    if (sortOrder !== 'DESC') params.sortOrder = sortOrder;
    setSearchParams(params, { replace: true });
  }, [status, search, area, page, sortBy, sortOrder, setSearchParams]);

  // Fetch customer settings to get dynamic status options
  const { data: customerSettings } = useQuery('customer-settings', async () => {
    const res = await api.get('/settings/customers');
    return res.data.data;
  });

  const { data, isLoading, isFetching } = useQuery(
    ['customers', page, search, status, area, sortBy, sortOrder],
    () => customerAPI.getAll({ page, limit: 20, search, status, area, sortBy, sortOrder }).then(res => res.data.data),
    {
      keepPreviousData: true,
      staleTime: 2 * 60 * 1000 // Keep data fresh for 2 minutes
    }
  );

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(column);
      setSortOrder('ASC');
    }
    setPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) {
      return <ChevronsUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortOrder === 'ASC'
      ? <ChevronUp className="w-4 h-4 text-primary-600" />
      : <ChevronDown className="w-4 h-4 text-primary-600" />;
  };

  const { data: areas } = useQuery('areas', () =>
    customerAPI.getAreas().then(res => res.data.data)
  );

  // Use status options from settings, fallback to hardcoded list
  const statusOptions = customerSettings?.statusOptions || Object.keys(STATUS_COLORS);

  const canCreate = ['admin', 'manager'].includes(user?.role);

  // Agent functions
  const customers = data?.customers || [];

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === customers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(customers.map(c => c.id));
    }
  };

  const handleGenerateODT = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }
    setGenerating(true);
    try {
      const response = await agentAPI.generateODT(selectedIds);
      const filename = response.headers['x-filename'] || 'Fiber Request.odt';
      const updatedCount = response.headers['x-updated-count'] || selectedIds.length;
      const blob = new Blob([response.data], { type: 'application/vnd.oasis.opendocument.text' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Fiber Request generated! ${updatedCount} customer(s) moved to "In Process"`);
      setSelectedIds([]);
      queryClient.invalidateQueries('customers');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate Fiber Request');
    } finally {
      setGenerating(false);
    }
  };

  const handleBatchStatus = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one customer');
      return;
    }
    if (!batchStatus) {
      toast.error('Please select a status');
      return;
    }
    setUpdatingStatus(true);
    try {
      const response = await agentAPI.batchUpdateStatus(selectedIds, batchStatus);
      toast.success(response.data.message);
      setSelectedIds([]);
      setBatchStatus('');
      queryClient.invalidateQueries('customers');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update statuses');
    } finally {
      setUpdatingStatus(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Customers</h1>
          <p className="text-gray-600 mt-1 text-sm md:text-base">Manage fiber connection customers</p>
        </div>
        {canCreate && (
          <Link to="/customers/new" className="btn btn-primary flex items-center justify-center gap-2 w-full sm:w-auto">
            <Plus size={20} />
            Add Customer
          </Link>
        )}
      </div>

      {/* Quick Status Filters - Horizontal scroll on mobile */}
      <div className="overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
        <div className="flex gap-2 min-w-max">
          <button
            onClick={() => setStatus('')}
            className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
              status === '' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setStatus('Temporary Disconnected')}
            className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
              status === 'Temporary Disconnected' ? 'bg-orange-500 text-white' : 'bg-orange-100 text-orange-800 hover:bg-orange-200'
            }`}
          >
            Temp. Disconnected
          </button>
          <button
            onClick={() => setStatus('Not Processed')}
            className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
              status === 'Not Processed' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
          >
            Not Processed
          </button>
          <button
            onClick={() => setStatus('In Process')}
            className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
              status === 'In Process' ? 'bg-yellow-500 text-white' : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
            }`}
          >
            In Process
          </button>
          <button
            onClick={() => setStatus('Pending Installation')}
            className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
              status === 'Pending Installation' ? 'bg-blue-500 text-white' : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
            }`}
          >
            Pending Installation
          </button>
          <button
            onClick={() => setStatus('Standby')}
            className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
              status === 'Standby' ? 'bg-purple-500 text-white' : 'bg-purple-100 text-purple-800 hover:bg-purple-200'
            }`}
          >
            Standby
          </button>
          <button
            onClick={() => setStatus('Active')}
            className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
              status === 'Active' ? 'bg-green-600 text-white' : 'bg-green-100 text-green-800 hover:bg-green-200'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setStatus('Canceled')}
            className={`px-3 py-1.5 md:px-4 md:py-2 rounded-lg font-medium transition-colors text-sm md:text-base whitespace-nowrap ${
              status === 'Canceled' ? 'bg-red-500 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'
            }`}
          >
            Canceled
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="label">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, phone, email, address, circuit..."
                className="input pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="label">Status</label>
            <select
              className="input"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All Statuses</option>
              {statusOptions.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Area</label>
            <select
              className="input"
              value={area}
              onChange={(e) => setArea(e.target.value)}
            >
              <option value="">All Areas</option>
              {areas?.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Cyta Request Creator - visible when Not Processed filter is active */}
      {showAgentBar && (
        <div className="card border-l-4 border-l-primary-500">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-800 mb-2">
                Cyta Request Creator ({selectedIds.length} selected)
              </p>
              <button
                onClick={handleGenerateODT}
                disabled={generating || selectedIds.length === 0}
                className="btn btn-primary flex items-center gap-2 w-full md:w-auto disabled:opacity-50"
              >
                {generating ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <Download size={18} />
                )}
                {generating ? 'Generating...' : 'Generate Fiber Request'}
              </button>
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-800 mb-2">Batch Status Update</p>
              <div className="flex gap-2">
                <select
                  className="input flex-1"
                  value={batchStatus}
                  onChange={(e) => setBatchStatus(e.target.value)}
                >
                  <option value="">Select status...</option>
                  {statusOptions.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <button
                  onClick={handleBatchStatus}
                  disabled={updatingStatus || selectedIds.length === 0 || !batchStatus}
                  className="btn btn-secondary flex items-center gap-2 disabled:opacity-50"
                >
                  {updatingStatus ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />
                  ) : (
                    <ArrowRightLeft size={18} />
                  )}
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer List */}
      <div className="card overflow-hidden p-0 md:p-0">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden divide-y divide-gray-200">
              {showAgentBar && customers.length > 0 && (
                <div
                  className="p-4 bg-gray-50 flex items-center gap-3 cursor-pointer"
                  onClick={toggleSelectAll}
                >
                  {selectedIds.length === customers.length ? (
                    <CheckSquare size={20} className="text-primary-600" />
                  ) : (
                    <Square size={20} className="text-gray-400" />
                  )}
                  <span className="font-medium text-sm text-gray-700">
                    Select All ({customers.length})
                  </span>
                </div>
              )}
              {data?.customers?.map((customer) => (
                <div
                  key={customer.id}
                  className={`p-4 cursor-pointer ${
                    showAgentBar && selectedIds.includes(customer.id)
                      ? 'bg-primary-50'
                      : 'hover:bg-gray-50 active:bg-gray-100'
                  }`}
                  onClick={() => showAgentBar ? toggleSelect(customer.id) : navigate(`/customers/${customer.id}`)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {showAgentBar && (
                        selectedIds.includes(customer.id) ? (
                          <CheckSquare size={20} className="text-primary-600 mt-0.5 flex-shrink-0" />
                        ) : (
                          <Square size={20} className="text-gray-400 mt-0.5 flex-shrink-0" />
                        )
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{customer.customerName}</p>
                        {customer.email && (
                          <p className="text-sm text-gray-500 truncate">{customer.email}</p>
                        )}
                      </div>
                    </div>
                    <span className={`status-badge ml-2 flex-shrink-0 ${STATUS_COLORS[customer.status]}`}>
                      {customer.status}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                    {customer.phone && (
                      <span className="flex items-center gap-1">
                        <Phone size={14} />
                        {customer.phone}
                      </span>
                    )}
                    {customer.area && (
                      <span className="flex items-center gap-1">
                        <MapPin size={14} />
                        {customer.area}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-400 mt-2">
                    {customer.requestDate && (
                      <span>Request: {formatDate(customer.requestDate)}</span>
                    )}
                    {customer.installationDate && (
                      <span>Installation: {formatDate(customer.installationDate)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {showAgentBar && (
                      <th className="py-3 px-4 w-10">
                        <div className="cursor-pointer" onClick={toggleSelectAll}>
                          {selectedIds.length === customers.length && customers.length > 0 ? (
                            <CheckSquare size={20} className="text-primary-600" />
                          ) : (
                            <Square size={20} className="text-gray-400" />
                          )}
                        </div>
                      </th>
                    )}
                    <th
                      className="text-left py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('customerName')}
                    >
                      <div className="flex items-center gap-1">
                        Customer
                        <SortIcon column="customerName" />
                      </div>
                    </th>
                    <th
                      className="text-left py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('phone')}
                    >
                      <div className="flex items-center gap-1">
                        Phone
                        <SortIcon column="phone" />
                      </div>
                    </th>
                    <th
                      className="text-left py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('area')}
                    >
                      <div className="flex items-center gap-1">
                        Area
                        <SortIcon column="area" />
                      </div>
                    </th>
                    <th
                      className="text-left py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        <SortIcon column="status" />
                      </div>
                    </th>
                    <th
                      className="text-left py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('requestDate')}
                    >
                      <div className="flex items-center gap-1">
                        Request Date
                        <SortIcon column="requestDate" />
                      </div>
                    </th>
                    <th
                      className="text-left py-3 px-4 font-medium text-gray-700 cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => handleSort('installationDate')}
                    >
                      <div className="flex items-center gap-1">
                        Installation Date
                        <SortIcon column="installationDate" />
                      </div>
                    </th>
                    <th className="text-right py-3 px-4 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.customers?.map((customer) => (
                    <tr
                      key={customer.id}
                      className={`border-t cursor-pointer ${
                        showAgentBar && selectedIds.includes(customer.id)
                          ? 'bg-primary-50'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => showAgentBar ? toggleSelect(customer.id) : navigate(`/customers/${customer.id}`)}
                    >
                      {showAgentBar && (
                        <td className="py-3 px-4">
                          {selectedIds.includes(customer.id) ? (
                            <CheckSquare size={20} className="text-primary-600" />
                          ) : (
                            <Square size={20} className="text-gray-400" />
                          )}
                        </td>
                      )}
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{customer.customerName}</p>
                          {customer.email && (
                            <p className="text-sm text-gray-600">{customer.email}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-700">{customer.phone || '-'}</td>
                      <td className="py-3 px-4 text-gray-700">{customer.area || '-'}</td>
                      <td className="py-3 px-4">
                        <span className={`status-badge ${STATUS_COLORS[customer.status]}`}>
                          {customer.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-700">
                        {customer.requestDate ? formatDate(customer.requestDate) : '-'}
                      </td>
                      <td className="py-3 px-4 text-gray-700">
                        {customer.installationDate ? formatDate(customer.installationDate) : '-'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Link
                          to={`/customers/${customer.id}`}
                          className="text-primary-600 hover:text-primary-700 font-medium"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {showAgentBar ? 'Details' : 'View'}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data?.pagination && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t gap-3">
                <div className="text-sm text-gray-700 text-center sm:text-left">
                  Showing {((page - 1) * data.pagination.limit) + 1} to{' '}
                  {Math.min(page * data.pagination.limit, data.pagination.total)} of{' '}
                  {data.pagination.total}
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="btn btn-secondary disabled:opacity-50 flex-1 sm:flex-none"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= data.pagination.totalPages}
                    className="btn btn-secondary disabled:opacity-50 flex-1 sm:flex-none"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Customers;
